import { isPlatformBrowser, Location } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

import { EnvironmentService } from 'src/services/environment/environment.service';
import type { WaitForGaCookieService } from 'src/services/personal-data-protection/implementations/wait-for-ga-cookie/wait-for-ga-cookie.service';
import { WAIT_FOR_GA_COOKIE } from 'src/services/personal-data-protection/tokens';
import { UtilsService } from 'src/services/utils/utils.service';
import type { IMarkerOptions } from 'src/shared/types/tracking';

declare global {
    interface Window {
        dataLayer: any[];
        markers: string[] | IMarkerOptions[];
    }
}

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly waitForGaCookieService = inject<WaitForGaCookieService>(WAIT_FOR_GA_COOKIE);
    private readonly locationService = inject(Location);
    private readonly utilsService = inject(UtilsService);
    private readonly cookieService = inject(CookieService);
    private readonly environmentService = inject(EnvironmentService);

    private readonly dynamicRemarketingEventName = 'dynx_remarketing_ready';
    private readonly subdirectoriesWithDynamicRemarketing = ['templates'];

    public init(): void {
        if (isPlatformBrowser(this.platformId)) {
            this.initDynamicRemarketing();
            this.waitForGaCookieService.wait();
        }
    }

    public setMarketoOptOut(isMarketoTrackingDisabled = true): void {
        const tenYearsInSeconds = 311040000;
        const currentDomainAndSubdomains = this.environmentService.environment.cookieDomain;

        this.cookieService.set('mkto_opt_out', 'true', {
            path: '/',
            expires: isMarketoTrackingDisabled ? tenYearsInSeconds : -1,
            domain: currentDomainAndSubdomains,
        });
    }

    public setMarkers(markers: string[] | IMarkerOptions[]): void {
        if (isPlatformBrowser(this.platformId)) {
            window.markers = markers;
        }
    }

    public getMarkers(): string[] | IMarkerOptions[] {
        if (isPlatformBrowser(this.platformId)) {
            return window.markers;
        }

        return [];
    }

    public getDynamicRemarketingEventName(): string {
        return this.dynamicRemarketingEventName;
    }

    private initDynamicRemarketing(): void {
        this.handleDynamicRemarketingEvent();

        this.locationService.onUrlChange(() => {
            this.handleDynamicRemarketingEvent();
        });
    }

    private handleDynamicRemarketingEvent(): void {
        const path = this.locationService.prepareExternalUrl(this.locationService.path(false));
        const clearedPath = this.utilsService.clearUrl(path);
        const pathnameArr = clearedPath.split('/');
        const subdirectory = pathnameArr[1];
        const isDynamicRemarketingEnabled = this.subdirectoriesWithDynamicRemarketing.indexOf(subdirectory) > -1;

        if (isDynamicRemarketingEnabled) {
            window.dataLayer = window.dataLayer || [];
            const eventValue = pathnameArr[pathnameArr.length - 2];
            const eventIndex = window.dataLayer
                .map((dataLayerEvent) => dataLayerEvent.event)
                .indexOf(this.dynamicRemarketingEventName);
            const isEventNotSet = eventIndex === -1;
            const event = {
                event: this.dynamicRemarketingEventName,
                dynxid: eventValue,
                dynxid2: subdirectory,
            };

            if (isEventNotSet) {
                window.dataLayer.push(event);
            } else {
                window.dataLayer[eventIndex] = event;
            }
        }
    }
}
